import * as $ from 'jquery';
import { BannerCarousel } from './components/banner-carousel';
import { TargetedNewsCarousel } from './components/targeted-carousel';
import { PromotionsGrid } from './components/promotions-grid';
import { ContentLoader } from './services/content-loader';
import { dispatchCustomEvent } from './helpers/custom-event';
import { Config, BannerOptions } from './config';
import { Messages } from './services/messages';
import { exposeConfig } from './services/global-helper';
import * as Utils from './helpers/utils';

declare var scrm: any;
declare var window: any;
declare var document: any;
declare var __SERVICE_ENDPOINT__: string;
declare var __FAILOVER_ENDPOINT__: string;

let defaultBannerOptions: BannerOptions = {
  defaultPackage: true
};

let commandQueue = scrm.q || [];
let immediateExecutionCommands = ['config'];
let delayedCommands = [];
let configured = false;
let config: Config;

const SESSION_COOKIE_NAME = 'TARGETED_NEWS';
const ENDPOINT_URL = __SERVICE_ENDPOINT__; // webpack injected
const FAILOVER_URL = __FAILOVER_ENDPOINT__; // webpack injected
const COMPONENT_TYPES = {
  BANNER: 'banner',
  PROMOTIONSGRID: 'promotions-grid',
  TARGETEDNEWS: 'targeted-news'
};

/**
 * These are default options applied to banner type if data-options are not provided
 */
const defaultBannerSlickOptions: JQuerySlickOptions = {
  dots: false,
  arrows: true,
  autoplay: true,
  autoplaySpeed: 7000,
  infinite: true,
  lazyLoad: 'progressive',
  speed: 500,
  fade: true,
  cssEase: 'linear',
  pauseOnFocus: false,
  draggable: false
};

/**
 * Execute a command
 * @param command
 */
let executor = (command) => {
  const [commandStr, ...args] = command;

  // don't execute config again if we are already configured
  if (configured && commandStr === 'config') {
    return;
  }

  // if we are not configured, and the current command is not config, bucket this command for later execution (after we execute config)
  if (!configured && commandStr !== 'config') {
    delayedCommands.push(command);
    return;
  }

  console.log('Executing', commandStr);

  switch (commandStr) {
    case 'config':
      config = { ...args[0], endpoint: ENDPOINT_URL, failover: FAILOVER_URL };
      configured = true;
      exposeConfig(config);
      executeDelayedCommands();
      break;

    case 'startTargetedNews':
      loadTargetedNewsContent();
      break;

    case 'loadZones':
      const zones = args[0];
      if (Utils.isArray(zones)) {
        loadBannerContent(zones);
      }
      break;

    case 'loadZone':
      const zoneData = args[0];
      if (zoneData) {
        const { name, label } = zoneData;
        loadBannerContent([name], label);
      }
      break;
  }
};

const loadBannerContent = (zones?, label?) => {
  const { BANNER, PROMOTIONSGRID } = COMPONENT_TYPES;
  const selectors = document.querySelectorAll(buildQuerySelectors([BANNER, PROMOTIONSGRID]));
  let zoneToComponentMap = Utils.toZoneComponentMap(selectors);
  let tempZoneToComponentMap = {};

  // If zones are passed in, we will load content only for those zones
  // If no zones passed in, we will attempt to load content for all zones found on the page
  if (zones && zones.length > 0) {
    zones
      .map(zone => zone.toUpperCase())
      .forEach(zone => {
        const mapItem = zoneToComponentMap[zone];
        if (mapItem) {
          tempZoneToComponentMap[zone] = mapItem;
        }
      });
    zoneToComponentMap = tempZoneToComponentMap;
  }

  if (zoneToComponentMap) {
    let zoneIds = Object.keys(zoneToComponentMap), // DOM zone ids
      loader = new ContentLoader(config);

    if (zoneIds && zoneIds.length > 0) {
      zoneIds = zoneIds.map(zoneId => zoneId.toUpperCase());
      // get the content details for each zone
      loader.getZoneContentDetailsByZoneId(zoneIds, label)
        .done(response => {
          try {
            let { zoneContents, resolverUrl, globalProperties, currencies } = response;
            if (zoneContents) {
              zoneIds.forEach(zoneId => {
                let { urls } = zoneContents[zoneId] || [];
                if (urls && urls.length > 0) {
                  loader.getPackageMarkup(...urls)
                    .done((...responses) => {
                      console.log('Retrieved all packages successfully');
                      if (responses && responses.length > 0) {
                        const { options } = zoneToComponentMap[zoneId];
                        const { autoplaySpeed } = zoneContents[zoneId];
                        let contentItems = [];
                        if (responses[0].constructor === Array) { // multiple package requests resolved [[],[]]
                          contentItems = responses
                            .map(response => response.slice(0, 1))
                            .reduce((urls, next) => urls.concat(next));
                          console.log('multiple packages loaded', contentItems);
                        } else { // single package request resolved []
                          console.log('single package loaded', [responses[0]]);
                          contentItems = [responses[0]];
                        }
                        // if no options are specified and an autoplaySpeed exists
                        if (!options && autoplaySpeed) {
                          // merge the autoplaySpeed options into the default options
                          const opts = { ...defaultBannerSlickOptions, autoplaySpeed };
                          // and set as that individual zones options
                          zoneToComponentMap[zoneId].options = opts;
                        }
                        // initialize class based on component type...
                        const props = getComponentProps(zoneToComponentMap[zoneId], response);
                        resolveComponentType(props, contentItems);
                      }
                    })
                    .fail(() => {
                      console.log('Failed to retrieve all packages for', zoneId);
                    });
                }
              });
            }
          } catch (error) {
            console.log('Default packages not available for', zoneIds);
          }
        })
        .fail(() => {

          const bannerOptions = { ...defaultBannerOptions, ...config.bannerOptions };

          // no default package should show. Inform the client of no content
          if (!bannerOptions.defaultPackage) {
            console.log('Not showing a default package');
            return;
          }

          console.error('Failed to get zone content details, attempting failover for zones', JSON.stringify(zoneIds));
          zoneIds.forEach(zoneId => {
            // request default zone content url for each zone
            loader.getZoneDefaultPackage(zoneId)
              .done(response => {
                let { contentUrl, resolverUrl, globalProperties, currencies, suppressDefault } = response;
                if (contentUrl && !suppressDefault) {
                  console.log('Retrieved default package content url for', zoneId);
                  // request default zone package markup
                  loader.getPackageMarkup(contentUrl)
                    .done(response => {
                      console.log('Retrieved default package contents for', zoneId);
                      const props = getComponentProps(zoneToComponentMap[zoneId], response);
                      resolveComponentType(props, [response]);
                    })
                    .fail(() => {
                      console.error('Failed to retrieve content for default package', contentUrl);
                    });
                }
              })
              .fail(() => {
                console.error('Failed to retieve default package for', zoneId);
              });
          });
        });
    }
  }
};

let loadTargetedNewsContent = () => {

  // Only show targeted news once per session
  if (Utils.getCookie(SESSION_COOKIE_NAME) === '') {
    return;
  }

  // ZONEID: { componentType: 'targeted-news', node: HTMLElement }
  const targetedNewsNodes = document.querySelectorAll('scrm-content[data-component-type="targeted-news"]');
  const zoneToComponentMap = Utils.toZoneComponentMap(targetedNewsNodes);

  if (zoneToComponentMap) {
    console.log('Found Targeted DOM components', zoneToComponentMap);
    let targetedZoneIds = Object.keys(zoneToComponentMap), // DOM zone ids
      loader = new ContentLoader(config);

    if (targetedZoneIds && targetedZoneIds.length > 0) {
      targetedZoneIds = targetedZoneIds.map(zoneId => zoneId.toUpperCase());

      loader.getZoneContentDetailsByZoneId(targetedZoneIds)
        .done(response => {
          try {
            let { zoneContents, resolverUrl, globalProperties, currencies } = response;
            if (zoneContents) {
              targetedZoneIds.forEach(zoneId => {
                let { urls } = zoneContents[zoneId] || [];
                if (urls && urls.length > 0) {
                  loader.getPackageMarkup(...urls)
                    .done((...responses) => {
                      console.log('Retrieved all packages successfully');
                      if (responses && responses.length > 0) {
                        let contentItems = [];
                        if (responses[0].constructor === Array) { // multiple package requests resolved [[],[]]
                          contentItems = responses
                            .map(response => response.slice(0, 1))
                            .reduce((urls, next) => urls.concat(next));
                          console.log('multiple packages loaded', contentItems);
                        } else { // single package request resolved []
                          contentItems = [responses[0]];
                          console.log('single package loaded', contentItems);
                        }

                        new TargetedNewsCarousel({
                          wrapperEl: zoneToComponentMap[zoneId].node,
                          data: {
                            currencies: currencies,
                            globalProperties: globalProperties,
                            resolverUrl: resolverUrl,
                            items: contentItems
                          },
                          onComponentMount: () => {
                            // TODO These should all be encapulated in an event emitter.
                            dispatchCustomEvent(window, 'scrmtargetedshow');
                            Utils.callHost(Messages.CONTENT.READY);
                          },
                          slickOptions: {
                            dots: false,
                            arrows: false,
                            autoplay: true,
                            autoplaySpeed: 7000,
                            infinite: true,
                            lazyLoad: 'progressive',
                            speed: 500,
                            fade: true,
                            cssEase: 'linear',
                            pauseOnFocus: false,
                            draggable: false
                          },
                          thumbnailSlickOptions: {
                            slidesToShow: 4,
                            autoplay: false,
                            focusOnSelect: true,
                            arrows: false
                          }
                        });

                        Utils.createSessionCookie(SESSION_COOKIE_NAME);
                      }
                    })
                    .fail(() => {
                      console.log('Failed to retrieve all packages for', zoneId);
                      Utils.callHost(Messages.CONTENT.NONE);
                    });
                }
              });
            }
          } catch (e) {
            console.log('Targeted news component has no default content', targetedZoneIds);
            Utils.callHost(Messages.CONTENT.NONE);
          }
        }) // If no targeted news content found, emit no content message
        .fail(() => Utils.callHost(Messages.CONTENT.NONE));
    }
  }
};

/**
 * Concatenates & returns a string of query selectors grouped together.
 * @param componentTypes an array of component types.
 */
let buildQuerySelectors = (componentTypes: Array<string>) => {
  let querySelector = '';
  componentTypes.forEach((val, idx, array) => {
    const node = `scrm-content[data-component-type="${val}"]`;
    const seperator = `${(idx !== array.length - 1) ? ', ' : ''}`;
    querySelector += `${node}${seperator}`;
  });
  return querySelector;
};

const getComponentProps = (componentMap: { [index: string]: any }, data: { [index: string]: any }) => {
  const { globalProperties, resolverUrl, currencies } = data;
  const { node, options, componentType } = componentMap;
  return {
    componentType,
    componentParams: {
      wrapperEl: node,
      data: { globalProperties, resolverUrl, currencies },
      onComponentMount: () => {
        Utils.callHost(Messages.CONTENT.READY);
      },
      slickOptions: options || defaultBannerSlickOptions
    }
  };
};

/**
 * Performs a switch on the component type and initializes
 * the component class based on the component type passed in.
 * @param props the component properties.
 * @param items optional content items.
 */
const resolveComponentType = (props: { [index: string]: any }, items = []) => {
  const { wrapperEl, slickOptions, onComponentMount, data } = props.componentParams;
  switch (props.componentType) {
    case COMPONENT_TYPES.BANNER:
      new BannerCarousel({
        wrapperEl: wrapperEl,
        data: { ...data, items },
        slickOptions: slickOptions,
        onComponentMount: onComponentMount
      });
      break;
    case COMPONENT_TYPES.PROMOTIONSGRID:
      new PromotionsGrid({
        wrapperEl: wrapperEl,
        data: { ...data, items },
        onComponentMount: onComponentMount
      });
      break;
    default:
      break;
  }
};

// commands arriving after the bundle load can be handled immediately by executor
let bootstrap = () => {
  console.log('bootstrapping executor');
  scrm = function () {
    return executor([].slice.call(arguments));
  };
  dispatchCustomEvent(window, 'scrmready');
};

/**
 * The commands in queue are prioritised and executed
 */
const schedulePreloadQueueExecution = () => {
  commandQueue
    .map(command => [].slice.call(command)) // convert arguments (array-like) to a real Array
    .map(queue => toPrioritisedQueue(queue))
    .forEach(command => executor(command));
};

const executeDelayedCommands = () => {
  delayedCommands.forEach(command => {
    console.log('executing delayed command ', command);
    executor(command);
  });
  delayedCommands = [];
};

/**
 * Sort the queue such that immediate execution commands are first
 * @param queue the command queue
 */
const toPrioritisedQueue = (queue) => {
  let priority = [], rest = [];
  queue
    .forEach(item => {
      if (immediateExecutionCommands.indexOf(item[0]) !== -1) {
        priority.push(item);
      }
      else {
        rest.push(item);
      }
    });

  return [...priority, ...rest];
};

const reloadableZones = () => {
  return document.querySelectorAll('scrm-content[data-reload-on-auth]');
};

const onRamSiteInfoEvt = (evt: any) => {
  console.log('[onRamSiteInfoEvt]', evt);
  if (evt && evt.detail) {
    const { brand, licenseDomain: site, ipCountry, country: c } = evt.detail;
    const endpoint = ENDPOINT_URL;
    const failover = FAILOVER_URL;
    const country = ipCountry || c || 'xy'; // fallback to 'xy' if no ipCountry/country exists
    const platform = Utils.detectPlatform();
    const language = Utils.getCookie('LANG') || 'en';
    const intid = Utils.getCookie('WIID') ? Utils.getCookie('WBID') : Utils.getCookie('XIID');
    if (endpoint && failover && brand && site && country && platform && language) {
      scrm('config', { endpoint, failover, brand, site, country, platform, language, intid });
      window.removeEventListener('ramSiteInfo', onRamSiteInfoEvt);
    }
  }
};

const onRamLoggedInEvt = () => {
  reloadableZones().forEach(zone => {
    if (Utils.isAuthenticated() && zone.getAttribute('loggedIn')
      || !Utils.isAuthenticated() && !zone.getAttribute('loggedIn')) {
      return;
    }
    scrm('loadZone', { name: zone.getAttribute('data-zone-id') });
    zone.setAttribute('loggedIn', 'loggedIn');
  });
};

const onRamLoggedOutEvt = () => {
  reloadableZones().forEach(zone => {
    if (Utils.isAuthenticated() && !zone.getAttribute('loggedIn')) {
      return;
    }
    scrm('loadZone', { name: zone.getAttribute('data-zone-id') });
    zone.removeAttribute('loggedIn', 'loggedIn');
  });
};

schedulePreloadQueueExecution();
bootstrap();

window.addEventListener('loggedIn', onRamLoggedInEvt);
window.addEventListener('loggedOut', onRamLoggedOutEvt);
window.addEventListener('ramSiteInfo', onRamSiteInfoEvt);

window.addEventListener('ramReady', () => {
  console.log('Received RAM Ready event');
  dispatchCustomEvent(document, 'ramSiteInfoRequest');
});

// inform ram we are ready, they will re-fire the ramrunscrm event
window.addEventListener('ramrunscrm', () => {
  console.log('Received start command from RAM for targeted news');
  console.log('dispatch event to RAM to create the modal DOM');
  scrm('startTargetedNews', {});
});

if (!configured) {
  console.log('Not configured, dispatching ramSiteInfoRequest');
  dispatchCustomEvent(document, 'ramSiteInfoRequest');
}


// WEBPACK FOOTER //
// ./src/index.ts